import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Check, Lock, ExternalLink, CreditCard } from 'lucide-react';

const LABELS = {
  0: 'Free',
  9: 'Tip Jar',
  27: 'Supporter',
  49: 'Champion',
  97: 'VIP',
};

export default function Unlock() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [amount, setAmount] = useState(27);
  const [custom, setCustom] = useState(false);
  const [customVal, setCustomVal] = useState('');
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState('choose'); // 'choose' | 'pay' | 'done'
  const [method, setMethod] = useState(null);

  useEffect(() => {
    api.get('/settings/payments').then(r => setSettings(r.data)).catch(()=>{});
  }, []);

  const effectiveAmount = custom ? Math.max(0, parseFloat(customVal) || 0) : amount;

  const handleFree = async () => {
    if (!user) { nav('/signup'); return; }
    setProcessing(true);
    try {
      await api.post('/payments/intent', { amount: 0, method: 'free' });
      await refresh();
      setStep('done');
      setTimeout(() => nav('/dashboard'), 1500);
    } finally { setProcessing(false); }
  };

  const handleMethod = async (m) => {
    if (!user) { nav('/signup'); return; }
    setMethod(m);
    setStep('pay');
  };

  const confirmPaid = async () => {
    setProcessing(true);
    try {
      await api.post('/payments/intent', { amount: effectiveAmount, method });
      await refresh();
      setStep('done');
      setTimeout(() => nav('/dashboard'), 1500);
    } finally { setProcessing(false); }
  };

  const venmoLink = () => {
    const handle = settings?.venmo_handle || 'mickeytrivett';
    const note = encodeURIComponent('Headspace Course');
    return `https://venmo.com/${handle}?txn=pay&amount=${effectiveAmount}&note=${note}`;
  };
  const cashappLink = () => {
    const handle = settings?.cashapp_handle || 'mickeytrivett';
    return `https://cash.app/$${handle}/${effectiveAmount}`;
  };

  if (!user) {
    return (
      <div className="kc-container py-16 max-w-xl text-center">
        <h1 className="font-display uppercase text-4xl">Sign up first</h1>
        <p className="text-muted-kc mt-2">Create a free account so we can track your unlocked access and progress.</p>
        <div className="mt-5 flex justify-center gap-2"><a href="/signup" className="h-11 px-5 inline-flex items-center rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold">Sign up</a></div>
      </div>
    );
  }

  if (user.has_paid) {
    return (
      <div className="kc-container py-16 max-w-xl text-center">
        <div className="font-display uppercase text-4xl">You're <span className="text-gold">unlocked</span>.</div>
        <p className="text-muted-kc mt-2">All 18 lessons are available. Continue from your dashboard.</p>
        <a href="/dashboard" className="mt-5 inline-flex h-11 px-5 items-center rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold">Go to dashboard</a>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="kc-container py-16 max-w-xl text-center">
        <Check className="text-gold mx-auto" size={48} />
        <div className="font-display uppercase text-4xl mt-4">You're <span className="text-gold">in</span>.</div>
        <p className="text-muted-kc mt-2">Full course unlocked. Redirecting to your dashboard…</p>
      </div>
    );
  }

  if (step === 'pay') {
    const label = method === 'venmo' ? 'Venmo' : 'CashApp';
    const link = method === 'venmo' ? venmoLink() : cashappLink();
    return (
      <div className="kc-container py-12 max-w-xl">
        <button onClick={() => setStep('choose')} className="text-sm text-muted-kc hover:text-ink mb-5" data-testid="pay-back">← Change method</button>
        <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Step 2 of 2</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Pay <span className="text-gold">${effectiveAmount}</span> via {label}</h1>
        <p className="text-muted-kc mt-2">Tap the button below to open {label}. Once you've sent the payment, come back here and click “I paid” to unlock.</p>
        <div className="mt-6 bg-white border border-kc rounded-[var(--kc-radius-md)] p-5">
          <div className="text-sm">Sending to: <span className="font-semibold">@{method === 'venmo' ? settings?.venmo_handle : settings?.cashapp_handle}</span></div>
          <div className="text-sm mt-1">Note: <span className="font-mono text-xs">Headspace Course</span></div>
        </div>
        <div className="mt-5 flex flex-col gap-2">
          <a href={link} target="_blank" rel="noreferrer" className="h-12 inline-flex items-center justify-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="pay-open-app">Open {label} <ExternalLink size={16}/></a>
          <button onClick={confirmPaid} disabled={processing} className="h-12 inline-flex items-center justify-center rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold hover:bg-black/90 transition-colors disabled:opacity-50" data-testid="pay-confirm">{processing ? 'Unlocking…' : 'I paid — unlock my course'}</button>
        </div>
        <p className="text-xs text-muted-kc mt-4">Honor system: we trust you. If you didn't actually send the payment, that's between you and your headspace.</p>
      </div>
    );
  }

  const tiers = settings?.suggested_tiers || [0, 9, 27, 49, 97];

  return (
    <div className="kc-container py-10 md:py-14 max-w-3xl">
      <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Step 1 of 2</div>
      <h1 className="font-display uppercase text-4xl md:text-6xl mt-2 leading-[0.95]">Pay what feels <span className="text-gold">right</span>.</h1>
      <p className="text-ink-2 mt-3 max-w-xl">You've completed the free preview. To unlock the rest of HEADSPACE — all 18 lessons, AI Coach, journal, exercises, and certificate — choose whatever amount feels right for you.</p>

      <div className="mt-7 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="unlock-tiers">
        {tiers.map((t) => (
          <button key={t} onClick={() => { setAmount(t); setCustom(false); }} className={`relative p-4 rounded-[var(--kc-radius-md)] border text-left transition-all ${!custom && amount === t ? 'border-gold bg-gold-soft' : 'border-kc-strong bg-white hover:border-gold'}`} data-testid={`unlock-tier-${t}`}>
            {t === 27 && <span className="absolute -top-2 left-3 text-[10px] tracking-wider font-bold uppercase bg-ink text-white px-2 py-0.5 rounded-full">Popular</span>}
            <div className="font-display text-3xl text-ink">${t}</div>
            <div className="text-xs text-muted-kc mt-1 uppercase tracking-wider">{LABELS[t] || 'Custom'}</div>
          </button>
        ))}
        <button onClick={() => setCustom(true)} className={`p-4 rounded-[var(--kc-radius-md)] border text-left transition-all ${custom ? 'border-gold bg-gold-soft' : 'border-kc-strong bg-white hover:border-gold'}`} data-testid="unlock-tier-custom">
          <div className="font-display text-3xl">$<input type="number" min={0} value={customVal} onChange={(e) => { setCustom(true); setCustomVal(e.target.value); }} placeholder="?" className="w-20 bg-transparent outline-none" data-testid="unlock-custom-input" /></div>
          <div className="text-xs text-muted-kc mt-1 uppercase tracking-wider">Your number</div>
        </button>
      </div>

      <div className="mt-8 text-[11px] tracking-[0.18em] uppercase text-muted-kc">Choose how to pay</div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        {effectiveAmount === 0 ? (
          <button onClick={handleFree} disabled={processing} className="p-5 rounded-[var(--kc-radius-md)] border border-gold bg-gold-soft text-left hover:bg-[color:var(--kc-gold)] transition-colors col-span-full" data-testid="unlock-free">
            <div className="font-display text-2xl">Unlock for free</div>
            <div className="text-sm mt-1">Yes, $0 is okay. Just take it seriously — your future self will thank you.</div>
          </button>
        ) : (
          <>
            {settings?.venmo_enabled && (
              <button onClick={() => handleMethod('venmo')} className="p-5 rounded-[var(--kc-radius-md)] border border-kc-strong bg-white text-left hover:border-gold transition-colors" data-testid="unlock-venmo">
                <div className="font-display text-2xl">Venmo</div>
                <div className="text-sm text-muted-kc mt-1">@{settings?.venmo_handle}</div>
              </button>
            )}
            {settings?.cashapp_enabled && (
              <button onClick={() => handleMethod('cashapp')} className="p-5 rounded-[var(--kc-radius-md)] border border-kc-strong bg-white text-left hover:border-gold transition-colors" data-testid="unlock-cashapp">
                <div className="font-display text-2xl">CashApp</div>
                <div className="text-sm text-muted-kc mt-1">${settings?.cashapp_handle}</div>
              </button>
            )}
            {settings?.stripe_enabled ? (
              <button onClick={() => handleMethod('stripe')} className="p-5 rounded-[var(--kc-radius-md)] border border-kc-strong bg-white text-left hover:border-gold transition-colors" data-testid="unlock-stripe">
                <div className="font-display text-2xl flex items-center gap-2"><CreditCard size={20}/> Card</div>
                <div className="text-sm text-muted-kc mt-1">Stripe (secure)</div>
              </button>
            ) : (
              <div className="p-5 rounded-[var(--kc-radius-md)] border border-dashed border-kc-strong bg-surface-2 text-left opacity-60" data-testid="unlock-stripe-coming">
                <div className="font-display text-2xl flex items-center gap-2"><CreditCard size={20}/> Card</div>
                <div className="text-sm text-muted-kc mt-1 flex items-center gap-1"><Lock size={12}/> Coming soon</div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="mt-8 text-xs text-muted-kc">By unlocking you agree this is honor-based. You can also refer friends — the goal is to help people, not gate them.</div>
    </div>
  );
}
